import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Gift, Plus, Star, Edit, Trash2, Eye, ExternalLink, FileText,
  Video, BookOpen, Lightbulb, Settings, FolderPlus
} from "lucide-react";

export function ResourceVaultZone({
  user,
  resources,
  resourceCategories,
  resourceCategory,
  setResourceCategory,
  fetchResources,
  expandedResourceId,
  setExpandedResourceId,
  setShowAddResource,
  handleEditResource,
  handleDeleteResource,
  setShowRequestRecommendation,
  fetchMyRecommendations,
  fetchResourceCategories,
  API,
  authFetch
}) {
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await authFetch(`${API}/community/resources/categories`, {
        method: "POST", body: JSON.stringify({ name: newCatName.trim() })
      });
      if (res.ok) { toast.success("Category added"); setNewCatName(""); if (fetchResourceCategories) fetchResourceCategories(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Failed to add category"); }
  };

  const deleteCategory = async (catId) => {
    if (!confirm("Delete this category? Resources in it won't be deleted.")) return;
    try {
      const res = await authFetch(`${API}/community/resources/categories/${catId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Category deleted"); if (fetchResourceCategories) fetchResourceCategories(); }
    } catch { toast.error("Failed"); }
  };
  return (
    <>
    <div className="space-y-6">
      {/* How-to Instructions Card */}
      <Card className="bg-gradient-to-r from-amber-500/10 to-neutral-900/50 border-amber-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2">How to Use the Resource Vault</h3>
              <ul className="text-sm text-neutral-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 font-bold">1.</span>
                  <span><strong>Browse categories</strong> on the left to find templates, guides, funding resources, and more</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 font-bold">2.</span>
                  <span><strong>Click "Open"</strong> on any resource to access the document, video, or external link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 font-bold">3.</span>
                  <span><strong>Look for Featured items</strong> (gold badge) - these are consultant-recommended resources</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 font-bold">4.</span>
                  <span><strong>Need a tool recommendation?</strong> Click the button below to request personalized advice</span>
                </li>
              </ul>
              <Button
                onClick={() => { setShowRequestRecommendation(true); fetchMyRecommendations(); }}
                className="mt-4 bg-amber-500 text-black hover:bg-amber-400"
                data-testid="request-recommendation-btn"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Request Tool Recommendation
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Categories Sidebar */}
        <div className="lg:col-span-1">
          <Card className="bg-neutral-800/50 border-neutral-700 sticky top-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg">Categories</CardTitle>
                {user?.role === "consultant" && (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowCatManager(true)}
                      className="text-neutral-400 hover:text-white h-8 w-8 p-0"
                      title="Manage Categories"
                      data-testid="manage-resource-categories-btn"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setShowAddResource(true)}
                      className="bg-[#39FF14] text-black hover:bg-[#32e612]"
                      data-testid="add-resource-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              <Button
                variant="ghost"
                className={`w-full justify-start ${resourceCategory === "all" ? "bg-[#39FF14]/10 text-[#39FF14]" : "text-neutral-400"}`}
                onClick={() => { setResourceCategory("all"); fetchResources(); }}
              >
                All Resources
              </Button>
              {resourceCategories.map((cat) => (
                <Button
                  key={cat.id}
                  variant="ghost"
                  className={`w-full justify-between ${resourceCategory === cat.id ? "bg-[#39FF14]/10 text-[#39FF14]" : "text-neutral-400"}`}
                  onClick={() => { setResourceCategory(cat.id); fetchResources(cat.id); }}
                >
                  <span>{cat.name}</span>
                  <Badge variant="outline" className="border-neutral-600 text-neutral-400">
                    {cat.count}
                  </Badge>
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Resources Grid */}
        <div className="lg:col-span-3">
          {user?.role === "consultant" && (
            <div className="mb-4 flex justify-end">
              <Button
                onClick={() => setShowAddResource(true)}
                className="bg-[#39FF14] text-black hover:bg-[#32e612]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Resource
              </Button>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {resources.length > 0 ? resources.map((resource, idx) => (
              <Card key={idx} className="bg-neutral-800/50 border-neutral-700 hover:border-[#39FF14]/30 transition-all cursor-pointer" onClick={() => setExpandedResourceId(expandedResourceId === resource.resource_id ? null : resource.resource_id)}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#39FF14]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      {resource.resource_type === "video" ? (
                        <Video className="w-5 h-5 text-[#39FF14]" />
                      ) : resource.resource_type === "template" ? (
                        <FileText className="w-5 h-5 text-[#39FF14]" />
                      ) : (
                        <Gift className="w-5 h-5 text-[#39FF14]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-medium text-white">{resource.title}</h3>
                        {resource.is_featured && (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 flex-shrink-0">
                            <Star className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-neutral-400 mt-1 line-clamp-2">{resource.description}</p>

                      {/* Expanded Details */}
                      {expandedResourceId === resource.resource_id && (
                        <div className="mt-3 pt-3 border-t border-neutral-700 space-y-2">
                          <p className="text-sm text-neutral-300">{resource.description}</p>
                          <div className="flex items-center gap-2 text-xs text-neutral-500">
                            <Badge variant="outline" className="border-neutral-600 capitalize">{resource.resource_type || "link"}</Badge>
                            <Badge variant="outline" className="border-neutral-600 capitalize">{resource.category}</Badge>
                          </div>
                          {resource.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {resource.tags.map((tag, i) => (
                                <span key={i} className="text-xs bg-neutral-700 text-neutral-400 px-2 py-0.5 rounded">{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                        {resource.resource_url ? (
                          <Button
                            variant="link"
                            className="text-[#39FF14] p-0 h-auto"
                            onClick={() => window.open(resource.resource_url, "_blank")}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Open
                          </Button>
                        ) : (
                          <span className="text-xs text-neutral-500 flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {expandedResourceId === resource.resource_id ? "Viewing details" : "Click to view details"}
                          </span>
                        )}
                        {user?.role === "consultant" && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-neutral-400 hover:text-white p-1 h-auto"
                              onClick={() => handleEditResource(resource)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-neutral-400 hover:text-red-400 p-1 h-auto"
                              onClick={() => handleDeleteResource(resource.resource_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="col-span-2 text-center py-12 text-neutral-500">
                <Gift className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No resources in this category yet</p>
                {user?.role === "consultant" && (
                  <Button
                    onClick={() => setShowAddResource(true)}
                    className="mt-4 bg-[#39FF14] text-black hover:bg-[#32e612]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Resource
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

      {/* Category Manager Dialog */}
      <Dialog open={showCatManager} onOpenChange={setShowCatManager}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Resource Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="New category name" onKeyDown={e => e.key === "Enter" && addCategory()} data-testid="new-resource-cat-input" />
              <Button onClick={addCategory} disabled={!newCatName.trim()} className="bg-[#333333] text-white" data-testid="add-resource-cat-btn">
                <FolderPlus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {resourceCategories.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm text-neutral-900">{c.name}</p>
                    <p className="text-xs text-neutral-500 font-mono">{c.id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.count > 0 && <Badge variant="outline" className="text-xs">{c.count}</Badge>}
                    <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50" onClick={() => deleteCategory(c.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
